import { useState, useEffect, useRef, useCallback } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import * as Query from '../api/gql/query'

export type JobStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
export type JobType = 'DZI_PROCESSING' | 'PDF_GENERATION'

export interface Job {
  id: string
  type: JobType
  status: JobStatus
  progress: number
  currentStep: string | null
  totalSteps: number | null
  currentStepNum: number | null
  error: string | null
  createdAt: string
  updatedAt: string
  startedAt: string | null
  completedAt: string | null
  // DZI Processing fields (null for other job types)
  dziUrl?: string | null
  tilesBaseUrl?: string | null
  // PDF Generation fields (null for other job types)
  pdfUrl?: string | null
  pageCount?: number | null
  fileSize?: number | null
}

export interface UseJobProgressOptions {
  pollInterval?: number // default: 2000ms
  maxPollDuration?: number // default: 5 minutes
  onComplete?: (job: Job) => void
  onError?: (error: string) => void
}

export interface UseJobProgressReturn {
  job: Job | null
  loading: boolean
  error: Error | null
  isPolling: boolean
  startPolling: (jobId: string) => void
  stopPolling: () => void
}

const DEFAULT_POLL_INTERVAL = 2000 // 2 seconds
const DEFAULT_MAX_POLL_DURATION = 5 * 60 * 1000 // 5 minutes

export default function useJobProgress(options: UseJobProgressOptions = {}): UseJobProgressReturn {
  const apolloClient = useApolloClient()
  const {
    pollInterval = DEFAULT_POLL_INTERVAL,
    maxPollDuration = DEFAULT_MAX_POLL_DURATION,
    onComplete,
    onError,
  } = options

  const [job, setJob] = useState<Job | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [isPolling, setIsPolling] = useState(false)

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const currentJobIdRef = useRef<string | null>(null)

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    setIsPolling(false)
    startTimeRef.current = null
  }, [])

  // Fetch job status
  const fetchJobStatus = useCallback(
    async (jobId: string): Promise<Job | null> => {
      try {
        const { data } = await apolloClient.query({
          query: Query.JOB_STATUS,
          variables: { id: jobId },
          fetchPolicy: 'network-only',
        })
        return data?.job || null
      } catch (err: any) {
        console.error('[useJobProgress] Fetch error:', err)
        throw err
      }
    },
    [apolloClient]
  )

  // Start polling
  const startPolling = useCallback(
    (jobId: string) => {
      // Stop any existing polling
      stopPolling()

      setLoading(true)
      setError(null)
      setIsPolling(true)
      startTimeRef.current = Date.now()
      currentJobIdRef.current = jobId

      const poll = async () => {
        if (!currentJobIdRef.current) return

        try {
          const fetchedJob = await fetchJobStatus(currentJobIdRef.current)
          if (!fetchedJob) {
            console.log('[useJobProgress] Job not found')
            return
          }

          setJob(fetchedJob)
          setLoading(false)

          // Check if job is complete
          if (fetchedJob.status === 'COMPLETED') {
            stopPolling()
            onComplete?.(fetchedJob)
            return
          }

          // Check if job failed
          if (fetchedJob.status === 'FAILED' || fetchedJob.status === 'CANCELLED') {
            stopPolling()
            onError?.(fetchedJob.error || 'Job failed')
            return
          }

          // Check for timeout
          if (startTimeRef.current && Date.now() - startTimeRef.current > maxPollDuration) {
            stopPolling()
            setError(new Error('Polling timeout'))
            onError?.('Job is taking longer than expected. It may still complete in the background.')
            return
          }
        } catch (err: any) {
          // Don't stop polling on network errors, just log
          console.error('[useJobProgress] Poll error:', err)
        }
      }

      // Do an immediate first poll
      poll()

      // Start interval polling
      pollingIntervalRef.current = setInterval(poll, pollInterval)
    },
    [fetchJobStatus, stopPolling, pollInterval, maxPollDuration, onComplete, onError]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  return {
    job,
    loading,
    error,
    isPolling,
    startPolling,
    stopPolling,
  }
}
